import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LangProvider } from '../../hooks/useLang';
import { useLang } from '../../hooks/useLang';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import RiskModal from '../home/components/RiskModal';
import SubscribeModal from './components/SubscribeModal';
import { columnArticles, columnCategories, columnCollections } from '../../mocks/column';
import ColumnCollections from './components/ColumnCollections';

function ColumnContent() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [activeCategory, setActiveCategory] = useState('全部');
  const [riskModal, setRiskModal] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);
  const articleGridRef = useRef<HTMLDivElement>(null);

  const activeCollection = columnCollections.find(c => c.id === activeCollectionId) ?? null;

  const handleCollectionSelect = (id: string | null) => {
    setActiveCollectionId(id);
    setActiveCategory('全部');
    // Always scroll to article grid area after state update
    requestAnimationFrame(() => {
      setTimeout(() => {
        articleGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    });
  };

  const filtered = (() => {
    if (activeCollection) {
      const ids = new Set(activeCollection.articleIds);
      return columnArticles.filter(a => ids.has(a.id));
    }
    if (activeCategory !== '全部') {
      return columnArticles.filter(a => a.category === activeCategory);
    }
    return columnArticles;
  })();

  const featured = columnArticles.filter(a => a.featured).slice(0, 2);
  const latest = columnArticles.slice(0, 3);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const categoryColors: Record<string, string> = {
    '政策深析': 'bg-[#7C3AED]/10 text-[#7C3AED]',
    '合规实务': 'bg-[#059669]/10 text-[#059669]',
    '实操指南': 'bg-[#2563EB]/10 text-[#2563EB]',
    '风险警示': 'bg-[#DC2626]/10 text-[#DC2626]',
    '案例解析': 'bg-[#D97706]/10 text-[#D97706]',
    '行业动态': 'bg-[#7C3AED]/10 text-[#7C3AED]',
  };

  // Column category translations
  const catTranslations: Record<string, Record<string, string>> = {
    '全部': { en: 'All', fr: 'Tout', es: 'Todo', pt: 'Tudo', ja: 'すべて', de: 'Alle', ko: '전체', ar: 'الكل', ru: 'Все' },
    '政策深析': { en: 'Policy Analysis', fr: 'Analyse Politique', es: 'Análisis de Política', pt: 'Análise de Política', ja: '政策分析', de: 'Politikanalyse', ko: '정책 분석', ar: 'تحليل السياسات', ru: 'Анализ Политики' },
    '合规实务': { en: 'Compliance Practice', fr: 'Pratique de Conformité', es: 'Práctica de Cumplimiento', pt: 'Prática de Conformidade', ja: 'コンプライアンス実務', de: 'Compliance-Praxis', ko: '컴플라이언스 실무', ar: 'ممارسة الامتثال', ru: 'Практика Соответствия' },
    '实操指南': { en: 'Practical Guide', fr: 'Guide Pratique', es: 'Guía Práctica', pt: 'Guia Prático', ja: '実践ガイド', de: 'Praxisleitfaden', ko: '실무 가이드', ar: 'دليل عملي', ru: 'Практическое Руководство' },
    '风险警示': { en: 'Risk Warning', fr: 'Avertissement de Risque', es: 'Advertencia de Riesgo', pt: 'Aviso de Risco', ja: 'リスク警告', de: 'Risikowarnung', ko: '위험 경고', ar: 'تحذير المخاطر', ru: 'Предупреждение о Рисках' },
    '案例解析': { en: 'Case Analysis', fr: 'Analyse de Cas', es: 'Análisis de Casos', pt: 'Análise de Casos', ja: 'ケース分析', de: 'Fallanalyse', ko: '사례 분석', ar: 'تحليل الحالات', ru: 'Анализ Случаев' },
    '行业动态': { en: 'Industry News', fr: 'Actualités du Secteur', es: 'Noticias del Sector', pt: 'Notícias do Setor', ja: '業界ニュース', de: 'Branchennachrichten', ko: '업계 동향', ar: 'أخبار القطاع', ru: 'Новости Отрасли' },
  };

  const translateCat = (cat: string) => {
    if (lang === 'zh') return cat;
    return catTranslations[cat]?.[lang] ?? catTranslations[cat]?.['en'] ?? cat;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar onRiskModal={() => setRiskModal(true)} />
      <RiskModal open={riskModal} onClose={() => setRiskModal(false)} isFirstVisit={false} />
      <SubscribeModal open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-br from-[#1A0050] via-[#2E1065] to-[#1A0050] pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#7C3AED]/20 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#F59E0B]/10 rounded-full blur-[80px]"></div>
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '40px 40px' }}
          ></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-[#F59E0B]/15 border border-[#F59E0B]/25 text-[#F59E0B] text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
            <i className="ri-quill-pen-line"></i>
            {t('RWA99.cn · 站长独立观察专栏', 'RWA99.cn · Editor\'s Independent Column')}
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4 tracking-tight">
            清鉴
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">纵论</span>
          </h1>
          <p className="text-white/50 text-base font-medium mb-3 tracking-widest">ChaInsight Column</p>
          <p className="text-white/70 text-lg max-w-2xl mx-auto leading-relaxed mb-8">
            {t('独立视角，深度剖析。以清醒之鉴，纵论RWA行业的政策、合规与未来。', 'Independent perspective, deep analysis. With clear insight, examining RWA policy, compliance, and the future.')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setSubscribeOpen(true)}
              className="flex items-center gap-2 px-6 py-3 bg-[#F59E0B] text-[#1A0050] font-bold text-sm rounded-full hover:bg-[#FBBF24] transition-colors cursor-pointer whitespace-nowrap shadow-lg shadow-[#F59E0B]/25"
            >
              <i className="ri-mail-send-line"></i>
              {t('订阅专栏推送', 'Subscribe to Column')}
            </button>
            <div className="flex items-center gap-6 text-white/50 text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-article-line text-[#A78BFA]"></i>
                {columnArticles.length} {t('篇深度文章', 'Articles')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-[#A78BFA]"></i>
                {(columnArticles.reduce((s, a) => s + a.viewCount, 0) / 1000).toFixed(1)}k {t('次阅读', 'Views')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-heart-line text-[#F59E0B]"></i>
                {columnArticles.reduce((s, a) => s + a.likeCount, 0).toLocaleString()} {t('获赞', 'Likes')}
              </span>
            </div>
          </div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 mt-10">
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl px-6 py-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#7C3AED] flex items-center justify-center text-white font-serif font-black text-xl shadow-lg">
                清
              </div>
              <div>
                <p className="text-white font-bold text-base font-serif">清鉴元衡 先生</p>
                <p className="text-white/50 text-xs">Mr. ChaInsight</p>
              </div>
              <div className="w-px h-10 bg-white/15 mx-2"></div>
              <p className="text-white/60 text-xs max-w-48 leading-relaxed">
                {t('深耕RWA合规研究，立足成都，洞察全球数字金融变局', 'Deep RWA compliance research from Chengdu, observing global digital finance')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 lg:px-6 py-12">
        {/* Collections */}
        <ColumnCollections selectedId={activeCollectionId} onSelect={handleCollectionSelect} />

        {/* Featured Articles */}
        {!activeCollection && (
          <section className="mb-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black text-gray-900 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-fire-line text-[#F59E0B]"></i>
                </div>
                {t('精选长文', 'Featured Articles')}
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featured.map(article => (
                <article
                  key={article.id}
                  onClick={() => navigate(`/column/${article.id}`)}
                  className="group relative bg-gradient-to-br from-[#2E1065] to-[#1A0050] rounded-2xl p-6 cursor-pointer overflow-hidden hover:shadow-2xl hover:shadow-[#7C3AED]/20 transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#7C3AED]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${article.category === '风险警示' ? 'bg-red-500/20 text-red-300' : 'bg-[#F59E0B]/20 text-[#F59E0B]'}`}>
                        {t(article.category, article.category)}
                      </span>
                      <span className="text-white/40 text-xs">{formatDate(article.publishedAt)}</span>
                    </div>
                    <h3 className="text-white font-bold text-lg leading-tight mb-2 group-hover:text-[#F59E0B] transition-colors">{article.title}</h3>
                    <p className="text-white/50 text-xs font-medium mb-3 italic">{article.subtitle}</p>
                    <p className="text-white/65 text-sm leading-relaxed line-clamp-3">{article.abstract}</p>
                    <div className="flex items-center justify-between mt-5">
                      <div className="flex items-center gap-4 text-white/40 text-xs">
                        <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{article.viewCount.toLocaleString()}</span>
                        <span className="flex items-center gap-1"><i className="ri-heart-line"></i>{article.likeCount}</span>
                        <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.readTime}min</span>
                      </div>
                      <div className="flex items-center gap-1 text-[#F59E0B] text-xs font-semibold group-hover:gap-2 transition-all">
                        {t('深度阅读', 'Read More')} <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Article section anchor */}
        <div ref={articleGridRef}>
          {/* Collection filter banner */}
          {activeCollection && (
            <div
              className="mb-6 rounded-2xl p-5 flex items-center justify-between"
              style={{ background: `linear-gradient(135deg, ${activeCollection.themeColor}22, ${activeCollection.themeColor}11)`, border: `1px solid ${activeCollection.accentColor}30` }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: activeCollection.accentColor + '20', border: `1px solid ${activeCollection.accentColor}40` }}
                >
                  <i className={`${activeCollection.icon} text-lg`} style={{ color: activeCollection.accentColor }}></i>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-bold text-white px-2.5 py-0.5 rounded-full" style={{ background: activeCollection.accentColor }}>{t('column.collections')}</span>
                    <h3 className="font-black text-gray-900 text-base">{activeCollection.title}</h3>
                  </div>
                  <p className="text-gray-500 text-xs">{activeCollection.description} · {activeCollection.articleIds.length} {t('common.articles')}</p>
                </div>
              </div>
              <button
                onClick={() => handleCollectionSelect(null)}
                className="flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 px-3 py-2 rounded-full hover:border-gray-300 hover:text-gray-700 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line"></i>
                {t('column.clearFilter')}
              </button>
            </div>
          )}

          {/* Category Filter — hide when collection is active */}
          {!activeCollection && (
            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {columnCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeCategory === cat ? 'bg-[#2E1065] text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#7C3AED]/50 hover:text-[#7C3AED]'}`}
                >
                  {translateCat(cat)} {cat !== '全部' && <span className="ml-1 text-xs opacity-60">({columnArticles.filter(a => a.category === cat).length})</span>}
                </button>
              ))}
            </div>
          )}

          {/* Result count */}
          {activeCollection && (
            <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
              <i className="ri-filter-3-line text-[#7C3AED]"></i>
              <span><span className="font-bold text-gray-900">{filtered.length}</span> {t('common.articles')}</span>
            </div>
          )}

          {/* Article Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
            {filtered.length === 0 ? (
              <div className="col-span-3 flex flex-col items-center justify-center py-16 text-gray-400">
                <div className="w-12 h-12 flex items-center justify-center mb-3">
                  <i className="ri-file-search-line text-4xl text-gray-200"></i>
                </div>
                <p className="text-sm">{t('common.noData')}</p>
              </div>
            ) : filtered.map(article => (
              <article
                key={article.id}
                onClick={() => navigate(`/column/${article.id}`)}
                className="group bg-white rounded-2xl border border-gray-100 hover:border-[#7C3AED]/30 hover:shadow-xl hover:shadow-[#7C3AED]/5 transition-all duration-300 overflow-hidden cursor-pointer flex flex-col"
              >
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[article.category] || 'bg-gray-100 text-gray-600'}`}>
                      {t(article.category, article.category)}
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <i className="ri-time-line"></i>{article.readTime}min
                    </span>
                  </div>
                  <h3 className="text-gray-900 font-bold text-base leading-snug mb-2 group-hover:text-[#7C3AED] transition-colors line-clamp-2">{article.title}</h3>
                  <p className="text-gray-400 text-xs italic mb-3 line-clamp-1">{article.subtitle}</p>
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 flex-1">{article.abstract}</p>
                  <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-3 text-gray-400 text-xs">
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{(article.viewCount / 1000).toFixed(1)}k</span>
                      <span className="flex items-center gap-1"><i className="ri-heart-line"></i>{article.likeCount}</span>
                      <span className="flex items-center gap-1"><i className="ri-discuss-line"></i>{article.commentCount}</span>
                    </div>
                    <span className="text-xs text-gray-400">{formatDate(article.publishedAt)}</span>
                  </div>
                </div>
                <div className="px-6 pb-4">
                  <div className="flex flex-wrap gap-1.5">
                    {article.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-md">#{tag}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Latest & Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-[#2E1065] to-[#4C1D95] rounded-2xl p-6 text-white">
              <h3 className="text-lg font-bold mb-1">{t('关于本栏目', 'About This Column')}</h3>
              <div className="w-12 h-0.5 bg-[#F59E0B] mb-4"></div>
              <p className="text-white/70 text-sm leading-relaxed mb-4">
                {t('「清鉴纵论」是RWA99.cn站长「清鉴元衡」先生的个人深度观察专栏。专栏以严谨的学术态度和独立的分析视角，深度剖析RWA行业的政策演进、合规实务、典型案例与行业趋势，每篇文章均力求在长度、深度、独特性三个维度上达到行业标准。', '"ChaInsight Column" is the personal in-depth observation column of Mr. ChaInsight, editor of RWA99.cn. With rigorous academic standards and independent analytical perspectives, the column deeply analyzes RWA industry policy evolution, compliance practices, typical cases, and industry trends.')}
              </p>
              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { icon: 'ri-scales-line', label: t('独立视角', 'Independent'), desc: t('不受任何机构立场影响', 'Uninfluenced by any institution') },
                  { icon: 'ri-search-2-line', label: t('深度研究', 'In-depth'), desc: t('每篇均经严格事实核查', 'Every article rigorously fact-checked') },
                  { icon: 'ri-book-3-line', label: t('学术严谨', 'Rigorous'), desc: t('引用权威政策与案例来源', 'Citing authoritative sources') },
                  { icon: 'ri-compass-line', label: t('前瞻洞见', 'Forward-looking'), desc: t('聚焦行业中长期演进', 'Focused on long-term evolution') },
                ].map(item => (
                  <div key={item.label} className="bg-white/5 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${item.icon} text-[#F59E0B] text-sm`}></i>
                      <span className="text-white font-semibold text-sm">{item.label}</span>
                    </div>
                    <p className="text-white/50 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setSubscribeOpen(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#F59E0B] text-[#1A0050] font-bold text-sm rounded-full hover:bg-[#FBBF24] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                {t('订阅专栏，不错过任何深度文章', 'Subscribe Now')}
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2 text-sm">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-timer-line text-[#7C3AED]"></i></div>
                {t('最新文章', 'Latest Articles')}
              </h4>
              <div className="space-y-4">
                {latest.map((article, i) => (
                  <div
                    key={article.id}
                    onClick={() => navigate(`/column/${article.id}`)}
                    className="flex gap-3 cursor-pointer group"
                  >
                    <span className="text-2xl font-black text-gray-100 leading-none shrink-0 w-7 text-center">{i + 1}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 group-hover:text-[#7C3AED] transition-colors line-clamp-2 leading-snug">{article.title}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatDate(article.publishedAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h4 className="font-bold text-gray-900 mb-4 text-sm">{t('专栏数据', 'Column Stats')}</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: columnArticles.length, label: t('篇文章', 'Articles'), icon: 'ri-article-line' },
                  { val: `${(columnArticles.reduce((s, a) => s + a.viewCount, 0) / 1000).toFixed(0)}k`, label: t('总阅读', 'Views'), icon: 'ri-eye-line' },
                  { val: columnArticles.reduce((s, a) => s + a.likeCount, 0).toLocaleString(), label: t('获赞', 'Likes'), icon: 'ri-heart-line' },
                  { val: columnArticles.reduce((s, a) => s + a.commentCount, 0), label: t('评论', 'Comments'), icon: 'ri-discuss-line' },
                ].map(stat => (
                  <div key={stat.label} className="bg-[#7C3AED]/5 rounded-xl p-3 text-center">
                    <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1">
                      <i className={`${stat.icon} text-[#7C3AED] text-base`}></i>
                    </div>
                    <div className="text-xl font-black text-gray-900">{stat.val}</div>
                    <div className="text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default function ColumnPage() {
  return (
    <LangProvider>
      <ColumnContent />
    </LangProvider>
  );
}
